import React from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  getRoleDisplayName,
  type OldRole as Role,
  type RolePermissions,
} from "../../domain";
import { textStyles } from "@/lib/textStyles";

interface ViewAllRolesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roles: Role[];
  permissions: RolePermissions;
}

export const ViewAllRolesDialog: React.FC<ViewAllRolesDialogProps> = ({
  open,
  onOpenChange,
  roles,
  permissions,
}) => {
  const { t } = useTranslation("rolesPermissions");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[calc(100dvh-1rem)] flex-col overflow-hidden sm:max-w-[650px]">
        <DialogHeader className="flex flex-row items-center justify-between">
          <DialogTitle className="text-xl font-semibold">
            {t("viewAllRolesDialog.title")}
          </DialogTitle>
        </DialogHeader>

        <div className="mt-4 min-h-0 flex-1 space-y-8 overflow-y-auto">
          {roles.map((role) => {
            const permissionsData = permissions[role.name];
            return (
              <div key={getRoleDisplayName(role)} className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">
                      {role.name}
                    </h3>
                    <p className="text-xs text-gray-400">
                      {role.access} . {t("viewAllRolesDialog.members", { count: role.members })}
                    </p>
                  </div>
                </div>

                {permissionsData ? (
                  <div className="space-y-4 pl-2">
                    {Object.entries(permissionsData).map(
                      ([category, perms]) => (
                        <div key={category} className="space-y-2">
                          <h4 className="text-sm text-gray-600 font-medium">
                            {category}
                          </h4>
                          {perms.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {perms.map((perm) => (
                                <span
                                  key={perm}
                                  className="inline-flex w-fit max-w-full items-center justify-center rounded-lg bg-gray-100 px-2 py-[5px] text-center"
                                >
                                  <span className="break-words" style={textStyles.body}>
                                    {perm}
                                  </span>
                                </span>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-400">
                              {t("viewAllRolesDialog.noPermissions")}
                            </p>
                          )}
                        </div>
                      ),
                    )}
                  </div>
                ) : (
                  <div className="space-y-2 pl-2">
                    <div className="space-y-2">
                      <h4 className="text-sm text-gray-600 font-medium">
                        {t("viewAllRolesDialog.permissionsTitle")}
                      </h4>
                      {role.permissions.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {role.permissions.map((perm) => (
                            <span
                              key={perm}
                              className="inline-flex w-fit max-w-full items-center justify-center rounded-lg bg-gray-100 px-2 py-[5px] text-center"
                            >
                              <span className="break-words" style={textStyles.body}>
                                {perm}
                              </span>
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-400">
                          {t("viewAllRolesDialog.noPermissions")}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {role.name !== roles[roles.length - 1].name && (
                  <div className="border-t border-gray-200 pt-4"></div>
                )}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};
